import { FormControl, FormControlProps, FormHelperText } from '@mui/material';
import type { V2FieldSize } from '@onevision-resources/design-theme';
import { useId, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';

export type FormSelectProps = Pick<FormControlProps, 'sx' | 'fullWidth' | 'className'> & {
  label: string;
  options: Option[];
  required?: boolean | string;
  rules?: Record<string, unknown>;
  clearable?: boolean;
  showErrorText?: boolean;
  disabled?: boolean;
  placeholder?: string;
  size?: V2FieldSize;
  /** Runs after the form value is set, for side effects on other fields. */
  onChange?: (value: Option['value'], option: Option) => void;
} & CommonFormFieldProps<'select', string>;

const CLEARED_VALUE = '';

// The menu keys rows by non-empty strings, so a "None" option's empty value gets a stand-in.
const EMPTY_VALUE_KEY = '__empty__';

const toMenuValue = (value: Option['value']) =>
  value === '' ? EMPTY_VALUE_KEY : String(value);

/**
 * Form field for one pick from a static list; the form stores the picked option's `value`.
 * Reach for `FormAutocomplete` instead when the field needs multiple picks, server-side
 * search or load-more, a "create" row, grouped or rich rows (icon, avatar, description),
 * free text, or the whole option object in the form. Outside react-hook-form, use `SelectMenu`.
 */
export const FormSelect = ({
  name,
  label,
  options,
  required,
  rules,
  clearable = false,
  showErrorText = false,
  disabled = false,
  placeholder = '',
  size,
  onChange,
  sx,
  fullWidth,
  className,
  hidden,
  'data-testid': dataTestId,
}: FormSelectProps) => {
  const triggerId = useId();
  const errorTextId = useId();
  const { control, formState, getFieldState } = useFormContext();
  const errorMsg = getFieldState(name, formState).error?.message;
  const hasErrorMsg = typeof errorMsg === 'string';
  const showsErrorText = showErrorText && hasErrorMsg;

  const menuOptions = useMemo<SelectMenuOption[]>(
    () =>
      options.map((option) => ({
        value: toMenuValue(option.value),
        title: option.label,
        testId: option.testId,
      })),
    [options],
  );

  return (
    <FormControl
      sx={sx}
      fullWidth={fullWidth}
      className={className}
      hidden={hidden}
      data-testid={dataTestId}
      disabled={disabled}
      error={hasErrorMsg}
    >
      <FormFieldLabel htmlFor={triggerId}>{label}</FormFieldLabel>
      <Controller
        name={name}
        control={control}
        rules={{ ...rules, required }}
        render={({ field }) => {
          const pickedOption = options.find((option) => option.value === field.value);

          return (
            <>
              {/* The picked value in the DOM, as a native select keeps it, readable while disabled. */}
              <input type="hidden" name={name} value={String(field.value ?? '')} />
              <SelectMenu
                ref={field.ref}
                id={triggerId}
                label={label}
                variant="field"
                size={size}
                menuWidth="trigger"
                options={menuOptions}
                value={pickedOption ? toMenuValue(pickedOption.value) : null}
                placeholder={placeholder}
                disabled={disabled}
                error={hasErrorMsg}
                aria-describedby={showsErrorText ? errorTextId : undefined}
                clearable={clearable}
                onClear={() => field.onChange(CLEARED_VALUE)}
                onChange={(menuValue) => {
                  const option = options.find(
                    (candidate) => toMenuValue(candidate.value) === menuValue,
                  );
                  if (!option) {
                    return;
                  }
                  field.onChange(option.value);
                  onChange?.(option.value, option);
                }}
                onOpenChange={(open) => {
                  if (!open) {
                    field.onBlur();
                  }
                }}
              />
            </>
          );
        }}
      />
      {showsErrorText ? <FormHelperText id={errorTextId}>{errorMsg}</FormHelperText> : null}
    </FormControl>
  );
};
